"use client";

import { useEffect, useRef, useState } from "react";
import { Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [buttonHovered, setButtonHovered] = useState(false);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let width = 0;
    let height = 0;
    const resize = () => {
      width = canvas.width = canvas.offsetWidth;
      height = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    type Trail = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      length: number;
      opacity: number;
      hue: number;
      width: number;
      life: number;
      maxLife: number;
    };
    const trails: Trail[] = [];
    const spawnTrail = () => {
      const fromLeft = Math.random() > 0.5;
      const x = fromLeft ? -100 : width + 100;
      const y = Math.random() * height;
      const speed = 2 + Math.random() * 4;
      const vx = fromLeft ? speed : -speed;
      const vy = (Math.random() - 0.5) * 0.5;
      const maxLife = 120 + Math.random() * 80;
      trails.push({
        x,
        y,
        vx,
        vy,
        length: 80 + Math.random() * 160,
        opacity: 0.3 + Math.random() * 0.5,
        hue: Math.random() > 0.3 ? 195 : 39,
        width: 0.5 + Math.random() * 1.5,
        life: 0,
        maxLife
      });
    };
    let frame = 0;
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      frame++;
      if (frame % 18 === 0 && trails.length < 20) {
        spawnTrail();
      }
      for (let i = trails.length - 1; i >= 0; i--) {
        const t = trails[i];
        t.x += t.vx;
        t.y += t.vy;
        t.life++;
        const lifeRatio = t.life / t.maxLife;
        const fade = lifeRatio < 0.2 ? lifeRatio / 0.2 : lifeRatio > 0.8 ? (1 - lifeRatio) / 0.2 : 1;
        const grad = ctx.createLinearGradient(t.x - t.vx * (t.length / Math.abs(t.vx)), t.y, t.x, t.y);
        grad.addColorStop(0, `hsla(${t.hue}, 89%, 59%, 0)`);
        grad.addColorStop(1, `hsla(${t.hue}, 89%, 59%, ${t.opacity * fade})`);
        ctx.beginPath();
        ctx.moveTo(t.x - t.vx * (t.length / Math.abs(t.vx)), t.y);
        ctx.lineTo(t.x, t.y);
        ctx.strokeStyle = grad;
        ctx.lineWidth = t.width;
        ctx.stroke();
        if (t.life >= t.maxLife) {
          trails.splice(i, 1);
        }
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    const waitReady = () => {
      if (canvas.offsetWidth > 0 && canvas.offsetHeight > 0) {
        resize();
        animate();
      } else {
        setTimeout(waitReady, 50);
      }
    };
    waitReady();
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{
    backgroundColor: "#0D1B2A"
  }}>
      {}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{
      opacity: 0.7
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(60, 197, 243, 0.08) 0%, transparent 70%), radial-gradient(ellipse 40% 30% at 20% 80%, rgba(252, 178, 49, 0.05) 0%, transparent 60%)"
    }} />

      {}
      <div ref={contentRef} className="relative z-10 flex flex-col items-center text-center px-6 py-16 max-w-5xl mx-auto">
        {}
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-6 border text-sm font-default font-semibold tracking-wide" style={{
        backgroundColor: "rgba(60, 197, 243, 0.1)",
        borderColor: "rgba(60, 197, 243, 0.35)",
        color: "#3CC5F3",
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.6s ease, transform 0.6s ease"
      }}>
          <span className="inline-block w-2 h-2 rounded-full" style={{
          backgroundColor: "#3CC5F3",
          boxShadow: "0 0 6px 2px rgba(60, 197, 243, 0.8)",
          animation: "pulse 2s ease-in-out infinite"
        }} />
          AI Voice Assistant
        </div>

        {}
        <h1 className="font-default font-extrabold leading-none mb-5" style={{
        fontSize: "clamp(3rem, 10vw, 82pt)",
        letterSpacing: "-0.02em",
        color: "#FFFFFF",
        textShadow: "0 0 40px rgba(60, 197, 243, 0.6), 0 0 80px rgba(60, 197, 243, 0.3)",
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(30px)",
        transition: "opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s",
        animation: "cyanPulse 3s ease-in-out infinite"
      }}>
          Talk to PocketPal
        </h1>

        {}
        <p className="font-default text-lg md:text-2xl font-semibold mb-2 max-w-2xl" style={{
        color: "#FFFFFF",
        opacity: contentVisible ? 0.95 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s"
      }}>
          Your AI financial companion, always ready to listen.
        </p>

        <p className="font-default text-base md:text-lg mb-8 max-w-xl" style={{
        color: "rgba(255,255,255,0.65)",
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.7s ease 0.42s, transform 0.7s ease 0.42s"
      }}>
          Instant market intelligence. Real broker connections. Powered by voice.
        </p>

        {}
        <div ref={lineRef} className="w-full max-w-xs h-px mb-8 overflow-hidden" style={{
        background: "rgba(60, 197, 243, 0.15)"
      }}>
          <div style={{
          height: "1px",
          background: "linear-gradient(90deg, transparent, #3CC5F3, transparent)",
          boxShadow: "0 0 8px 2px rgba(60, 197, 243, 0.6)",
          width: lineVisible ? "100%" : "0%",
          transition: "width 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.2s"
        }} />
        </div>

        {}
        <div style={{
        opacity: contentVisible ? 1 : 0,
        transform: contentVisible ? "translateY(0)" : "translateY(20px)",
        transition: "opacity 0.7s ease 0.55s, transform 0.7s ease 0.55s"
      }}>
          <Button asChild className="rounded-full font-default font-bold text-lg px-10 py-6 h-auto border-0" onMouseEnter={() => setButtonHovered(true)} onMouseLeave={() => setButtonHovered(false)} style={{
          backgroundColor: "#FCB231",
          color: "#0D1B2A",
          boxShadow: buttonHovered ? "0 0 0 15px rgba(252, 178, 49, 0.18), 0 0 40px 8px rgba(252, 178, 49, 0.45)" : "0 0 0 0px rgba(252, 178, 49, 0), 0 4px 20px rgba(252, 178, 49, 0.3)",
          transition: "box-shadow 0.3s ease, transform 0.2s ease",
          transform: buttonHovered ? "scale(1.04)" : "scale(1)"
        }}>
            
          </Button>
        </div>

        {}
        <div className="mt-16 flex flex-col items-center gap-3" style={{
        opacity: contentVisible ? 1 : 0,
        transition: "opacity 0.8s ease 0.8s"
      }}>
          <div className="flex items-end gap-1" style={{
          height: "40px"
        }}>
            {[4, 8, 14, 20, 14, 26, 14, 20, 14, 8, 4].map((h, i) => <div data-index={i} key={i} className="rounded-full" style={{
            width: "4px",
            height: `${h}px`,
            backgroundColor: "#3CC5F3",
            opacity: 0.7,
            animation: `waveBar 1.2s ease-in-out ${i * 0.1}s infinite alternate`
          }} />)}
          </div>
          <span className="text-xs font-default tracking-widest uppercase" style={{
          color: "rgba(60, 197, 243, 0.6)"
        }}>
            Voice Ready
          </span>
        </div>
      </div>

      {}
      <style>{`
        @keyframes cyanPulse {
          0%, 100% { text-shadow: 0 0 40px rgba(60,197,243,0.6), 0 0 80px rgba(60,197,243,0.3); }
          50% { text-shadow: 0 0 60px rgba(60,197,243,0.9), 0 0 120px rgba(60,197,243,0.5); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.3); }
        }
        @keyframes waveBar {
          0% { transform: scaleY(0.4); opacity: 0.4; }
          100% { transform: scaleY(1.6); opacity: 1; }
        }
      `}</style>
    </section>;
}